import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
    Autocomplete,
    Box,
    Button,
    Grid,
    TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import countriesData from "../../apis/CountryStates";
import { maxPriceOptions, minPriceOptions, options, typeOptions } from "../../apis/utils";

const SearchBar = (props) => {
    const [states, setStates] = useState([]);
    const [filteredBedrooms, setFilteredBedrooms] = useState(options);
    const [filteredTypes, setFilteredTypes] = useState(typeOptions);
    const [filteredMinPrices, setFilteredMinPrices] = useState(props.statusType === 'For Sale' ? minPriceOptions : maxPriceOptions);
    const [filteredMaxPrices, setFilteredMaxPrices] = useState(props.statusType === 'For Sale' ? minPriceOptions : maxPriceOptions);
    const [locationOptions, setLocationOptions] = useState([]);
    const [isLocationDisabled, setIsLocationDisabled] = useState(false);
    const [defaultLocation, setDefaultLocation] = useState("");
    const stateCities = {
        "Andhra Pradesh": [
            "Vijayawada",
            "Kakinada",
            "Amaravathi"
        ],
        "Telangana": "Hyderabad"
    };

    const initialValues = {
        state: "",
        location: "",
        minPrice: "",
        maxPrice: "",
        propertyStatus: props.statusType,
        type: "",
        bedrooms: "",
    };

    const validationSchema = yup.object({
        state: yup.string(),
        location: yup.string(),
        minPrice: yup.string(),
        maxPrice: yup.string(),
        type: yup.string(),
        bedrooms: yup.string(),
    });

    const handleStateChange = (selectedState) => {
        formik.setFieldValue("state", selectedState);
        formik.setFieldValue("location", "");
        if (stateCities[selectedState]) {
            if (Array.isArray(stateCities[selectedState])) {
                setLocationOptions(stateCities[selectedState]);
                setIsLocationDisabled(false);
                setDefaultLocation("");
            } else {
                setLocationOptions([]);
                setIsLocationDisabled(true);
                setDefaultLocation(stateCities[selectedState]);
                formik.setFieldValue("location", stateCities[selectedState]);
            }
        } else {
            setLocationOptions([]);
            setIsLocationDisabled(false);
            setDefaultLocation("");
        }
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: (values) => {
            sessionStorage.setItem("searchParams", JSON.stringify(values));
            props.setSearchBack && props.setSearchBack(values);
        },
    });

    useEffect(() => {
        // Set states from India data
        const indiaData = countriesData.find((c) => c.name === "India");
        setStates(indiaData?.states || []);

        // Set India-specific filters
        setFilteredBedrooms([
            { id: 1, label: "1 BHK" },
            { id: 2, label: "2 BHK" },
            { id: 3, label: "3 BHK" },
            { id: 4, label: "4 BHK" },
        ]);
        setFilteredTypes([
            { id: 1, label: "Apartment" },
            { id: 2, label: "Villa" },
            { id: 3, label: "Plot" },
            { id: 4, label: "Independent House" },
        ]);
        setFilteredMinPrices([
            { id: 500000, label: "₹5,00,000" },
            { id: 1000000, label: "₹10,00,000" },
            { id: 1500000, label: "₹15,00,000" },
            { id: 2000000, label: "₹20,00,000" },
            { id: 3000000, label: "₹30,00,000" },
        ]);
        setFilteredMaxPrices([
            { id: 5000000, label: "₹50,00,000" },
            { id: 10000000, label: "₹1 Crore" },
            { id: 15000000, label: "₹1.5 Crore" },
            { id: 20000000, label: "₹2 Crore" },
        ]);
    }, [props.statusType]);

    return (
        <Box className='container'
            sx={{
                padding: 3,
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                borderRadius: 3,
                backgroundColor: "#fff",
            }}
        >
            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={6} md={4}>
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={states}
                            getOptionLabel={(option) => option.name}
                            value={states.find(s => s.name === formik.values.state) || null}
                            onChange={(event, value) => handleStateChange(value ? value.name : "")}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="State"
                                    error={formik.touched.state && Boolean(formik.errors.state)}
                                    helperText={formik.touched.state && formik.errors.state}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6} md={4}>
                        {locationOptions.length > 0 ? (
                            <Autocomplete
                                fullWidth
                                size="small"
                                options={locationOptions}
                                getOptionLabel={(option) => option}
                                value={formik.values.location || null}
                                onChange={(event, value) => formik.setFieldValue("location", value || "")}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Location"
                                        error={formik.touched.location && Boolean(formik.errors.location)}
                                        helperText={formik.touched.location && formik.errors.location}
                                    />
                                )}
                            />
                        ) : (
                            <TextField
                                fullWidth
                                size="small"
                                name="location"
                                value={isLocationDisabled ? defaultLocation : formik.values.location}
                                onChange={formik.handleChange}
                                placeholder="Location"
                                disabled={isLocationDisabled}
                                error={formik.touched.location && Boolean(formik.errors.location)}
                                helperText={formik.touched.location && formik.errors.location}
                            />
                        )}
                    </Grid>
                    <Grid item xs={6} md={4}>
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={filteredTypes}
                            getOptionLabel={(option) => option.label}
                            value={filteredTypes.find((type) => type.label === formik.values.type) || null}
                            onChange={(event, value) => formik.setFieldValue("type", value ? value.label : "")}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Property Type" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6} md={4}>
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={filteredMinPrices}
                            getOptionLabel={(option) => option.label}
                            value={filteredMinPrices.find((min) => min.id === formik.values.minPrice) || null}
                            onChange={(event, value) => formik.setFieldValue("minPrice", value ? value.id : "")}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Min Price" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6} md={4}>
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={filteredMaxPrices}
                            getOptionLabel={(option) => option.label}
                            value={filteredMaxPrices.find((max) => max.id === formik.values.maxPrice) || null}
                            onChange={(event, value) => formik.setFieldValue("maxPrice", value ? value.id : "")}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Max Price" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6} md={4}>
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={filteredBedrooms}
                            getOptionLabel={(option) => option.label}
                            value={filteredBedrooms.find((bed) => bed.label === formik.values.bedrooms) || null}
                            onChange={(event, value) => formik.setFieldValue("bedrooms", value ? value.label : "")}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Bedrooms" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Button sx={{
                            backgroundColor: "#d8b035",
                            "&:hover": {
                                backgroundColor: "#b88b29",
                            },
                            borderRadius: 2,
                        }}
                            variant="contained"
                            color="primary"
                            size="large"
                            fullWidth
                            startIcon={<SearchIcon />}
                            type="submit">
                            Search
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};

export default SearchBar;
